import { useEffect, useState, useRef } from 'react';
import { useInView, motion, useSpring } from 'framer-motion';
import { cn } from '@/lib/utils';

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  once?: boolean;
}

export const AnimatedCounter = ({
  value,
  duration = 2,
  decimals = 0,
  prefix = '',
  suffix = '',
  className,
  once = true,
}: AnimatedCounterProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once });
  const [displayValue, setDisplayValue] = useState(0);

  const springValue = useSpring(0, {
    duration: duration * 1000,
    bounce: 0,
  });

  useEffect(() => {
    if (isInView) {
      springValue.set(value);
    }
  }, [isInView, value, springValue]);

  useEffect(() => {
    const unsubscribe = springValue.on('change', (latest) => {
      setDisplayValue(latest);
    });
    return unsubscribe;
  }, [springValue]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {displayValue.toFixed(decimals)}
      {suffix}
    </span>
  );
};

// Animated Number with Roll Effect
interface AnimatedNumberProps {
  value: number;
  className?: string;
}

export const AnimatedNumber = ({ value, className }: AnimatedNumberProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  return (
    <span ref={ref} className={cn('inline-flex overflow-hidden', className)}>
      {String(value).split('').map((digit, index) => (
        <motion.span
          key={`${index}-${digit}`}
          initial={{ y: '100%' }}
          animate={isInView ? { y: 0 } : { y: '100%' }}
          transition={{
            duration: 0.5,
            delay: index * 0.1,
            ease: [0.215, 0.61, 0.355, 1],
          }}
        >
          {digit}
        </motion.span>
      ))}
    </span>
  );
};

// Stats Card with Animated Counter
interface StatCardProps {
  label: string;
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  icon?: React.ReactNode;
  trend?: { value: number; positive: boolean };
  className?: string;
}

export const StatCard = ({
  label,
  value,
  suffix,
  prefix,
  decimals = 0,
  icon,
  trend,
  className,
}: StatCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      className={cn(
        'p-6 rounded-xl bg-gray-900/50 border border-gray-800',
        'hover:border-primary/30 transition-colors',
        className
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-400">{label}</span>
        {icon && <div className="text-primary">{icon}</div>}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-bold text-white">
          <AnimatedCounter
            value={value}
            prefix={prefix}
            suffix={suffix}
            decimals={decimals}
            duration={2}
          />
        </span>
        {trend && (
          <span
            className={cn(
              'text-sm font-medium',
              trend.positive ? 'text-emerald-500' : 'text-red-500'
            )}
          >
            {trend.positive ? '+' : '-'}{Math.abs(trend.value)}%
          </span>
        )}
      </div>
    </motion.div>
  );
};

export default AnimatedCounter;
